/**
 * NotificationsPage
 *
 * Full-page notification center with filtering, mark-all-as-read,
 * and paginated notification list.
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 * Story: SCRUM-1619 - Build /notifications full-page view + wire dropdown button
 *
 * Uses the REST-based useNotifications() hook (not WebSocket).
 * Reuses NotificationItem component with showActions=true.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/notifications/useNotifications';
import NotificationItem from '../../components/notifications/NotificationItem';
import DashboardLayout from '../../components/layout/DashboardLayout';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'critical', label: 'Critical' },
  { key: 'high', label: 'High' },
];

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  const [activeFilter, setActiveFilter] = useState('all');
  const {
    notifications,
    pagination,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  // Fetch notifications when filter changes
  const loadNotifications = useCallback(() => {
    const filters = { page: 1, limit: 20 };

    if (activeFilter === 'unread') {
      filters.unreadOnly = true;
    } else if (activeFilter === 'critical') {
      filters.urgency = 'critical';
    } else if (activeFilter === 'high') {
      filters.urgency = 'high';
    }

    fetchNotifications(filters);
  }, [activeFilter, fetchNotifications]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Load more (next page)
  const handleLoadMore = useCallback(() => {
    if (!pagination || !pagination.page) {
      return;
    }
    const filters = { page: pagination.page + 1, limit: 20 };

    if (activeFilter === 'unread') {
      filters.unreadOnly = true;
    } else if (activeFilter === 'critical') {
      filters.urgency = 'critical';
    } else if (activeFilter === 'high') {
      filters.urgency = 'high';
    }

    fetchNotifications(filters);
  }, [activeFilter, pagination, fetchNotifications]);

  const hasMore = pagination && pagination.page < pagination.totalPages;

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
    // Reload if viewing unread filter
    if (activeFilter === 'unread') {
      loadNotifications();
    }
  }, [markAllAsRead, activeFilter, loadNotifications]);

  // Empty state messages per filter
  const emptyMessages = {
    all: 'You have no notifications yet.',
    unread: 'All caught up! No unread notifications.',
    critical: 'No critical notifications.',
    high: 'No high-priority notifications.',
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <DashboardLayout userRole={user?.primaryRole ?? 'guide'} onNavigate={handleNavigate}>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              className="text-sm text-brand-primary hover:text-brand-primary-dark font-medium"
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200" role="tablist">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              role="tab"
              aria-selected={activeFilter === filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeFilter === filter.key
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading && notifications.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
          </div>
        )}

        {/* Notification list */}
        {!loading && notifications.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <p className="text-lg">{emptyMessages[activeFilter]}</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onDelete={deleteNotification}
                showActions={true}
              />
            ))}
          </div>
        )}

        {/* Load more button */}
        {hasMore && (
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-brand-primary border border-brand-primary rounded-lg hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
